import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../apiClient";

export type CustomerGroup = {
  id: number;
  name: string;
  member_count: number;
};

export const customerGroupsKeys = {
  all: ["customerGroups"] as const,
};

export function useCustomerGroupsQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: customerGroupsKeys.all,
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const { data } = await apiClient.get<CustomerGroup[]>("/customer_groups");
      return data;
    },
  });
}

export function useCreateCustomerGroupMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const { data } = await apiClient.post("/customer_groups", { name });
      return data as CustomerGroup;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: customerGroupsKeys.all }),
  });
}

export function useBulkAssignGroupMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      group_id: number;
      customer_organization_ids: number[];
    }) => {
      const { data } = await apiClient.post(
        "/customer_groups/bulk_assign",
        payload,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: customerGroupsKeys.all }),
  });
}

export function useBulkRemoveGroupMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      group_id: number;
      customer_organization_ids: number[];
    }) => {
      const { data } = await apiClient.post(
        "/customer_groups/bulk_remove",
        payload,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: customerGroupsKeys.all }),
  });
}

export function useCreateAndAssignGroupMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      name: string;
      customer_organization_ids: number[];
    }) => {
      const { data } = await apiClient.post(
        "/customer_groups/create_and_assign",
        payload,
      );
      return data as CustomerGroup;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: customerGroupsKeys.all }),
  });
}
